import { Alert, AlertTitle } from "@mui/material";
import * as React from "react";

import { PageLayout } from "@/shared/components/PageLayout";
import { EphemeralWorkspaceStateContext } from "@/shared/components/visualization/WorkspaceCtx";
import { LayoutRecord, LayoutService } from "@/shared/domain/layouts";
import { determineTimeBounds } from "@/shared/domain/topics/determineTimeBounds";
import { getBrowser } from "@/shared/environment";
import { usePopupState } from "@/shared/hooks";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";
import {
  CURRENT_TIME_NS_FIELD,
  EARLIEST_TIME_NS_FIELD,
  registerLiveVisualizerState,
  useRegisterViewingContext,
} from "@/shared/services/ViewingContextService";
import { useVizState } from "@/shared/state/visualization";

import { DndContainer } from "../DndContainer";
import { Header } from "../Header";
import { DatasourceInfo } from "../Header/DatasourceInfo";
import { useAddFilesToWorkspace } from "../hooks/useAddFilesToWorkspace";
import { LAYOUT_CONFLICT_ERROR_NAME, useWorkspaceLoaderData } from "../loader";
import { PanelBoard } from "../PanelBoard";
import { Sidebar } from "../Sidebar";
import { Timeline } from "../Timeline";
import { sessionNotices, useWorkspaceState } from "../WorkspaceCtx";

import { useEnforceStorageQuota } from "./hooks/useEnforceStorageQuota";
import { useEventDeepLink } from "./hooks/useEventDeepLink";
import { useLayoutHydration } from "./hooks/useLayoutHydration";
import { useSuggestChromium } from "./hooks/useSuggestChromium";
import { useSyncTimerAndSearchParams } from "./hooks/useSyncTimerAndSearchParams";
import { useWorkspaceActionSubscriber } from "./hooks/useWorkspaceActionSubscriber";
import { LayoutConflictModal } from "./LayoutConflictModal";
import { type Notification, Notifications } from "./Notifications";
import { singleOrgIdForDataSources } from "./singleOrgIdForDataSources";
import styles from "./Workspace.module.css";
import {
  initialNotifications,
  notificationFor,
  withNewNotifications,
} from "./workspaceNotifications";

export function Workspace() {
  useEnforceStorageQuota();

  useEventDeepLink();

  const visualizerInitData = useWorkspaceLoaderData();
  const workspaceContext = useWorkspaceState();

  const vizState = useVizState();

  const { addFiles } = useAddFilesToWorkspace();
  useWorkspaceActionSubscriber(workspaceContext, addFiles);

  const dataSources = workspaceContext.files.map((file) => {
    return {
      type: "file" as const,
      id: file.file_id,
      record: file,
    };
  });

  // Folded from the same topics the Timeline's workspace-wide bounds use, so
  // an AI thread following the workspace reads t=0 as the visualizer's own
  // "Elapsed" display does. Not read through `useWorkspaceTimeBounds`: this
  // component provides the context that hook reads, so from here it would only
  // see an empty workspace. Absent until topics load, and for a workspace of
  // media files, which carry no topics at all.
  const { timer, topics } = workspaceContext;
  const earliestTimeNs = React.useMemo(
    () => determineTimeBounds(topics).earliest,
    [topics],
  );

  useRegisterViewingContext("visualizer", {
    dataSources,
    // The earliest loaded time goes on the reactive channel so consumers
    // outside this subtree — the chat's t=0 — re-render when the workspace
    // loads different files, rather than reading a value that silently went
    // stale.
    state: {
      ...(vizState as unknown as Record<string, unknown>),
      ...(earliestTimeNs !== undefined && {
        [EARLIEST_TIME_NS_FIELD]: earliestTimeNs.toString(),
      }),
    },
    // The visualizer is the only multi-resource view, so it resolves its own
    // cross-org ambiguity here: one org when every source agrees, else none.
    orgId: singleOrgIdForDataSources(dataSources),
  });

  // The playhead advances at tick rate during playback, so it bypasses the
  // reactive viewing-context registration above and is instead resolved
  // imperatively when a reader (the AI chat send path) asks for it. The
  // timer is a stable, internally-mutable object for the lifetime of the
  // workspace, so this closure never goes stale.
  React.useEffect(
    function registerLiveWorkspaceTime() {
      return registerLiveVisualizerState(() => {
        // Timer.currentTime yields 0n when no time span contains the current
        // position — its "no data" sentinel, never a real recording
        // timestamp. Contribute nothing in that case.
        const currentTimeNs = timer.currentTime;
        if (currentTimeNs === 0n) {
          return {};
        }
        return { [CURRENT_TIME_NS_FIELD]: currentTimeNs.toString() };
      });
    },
    [timer],
  );

  const [workspaceNotifications, setWorkspaceNotifications] = React.useState<
    Notification[]
  >(() => initialNotifications(visualizerInitData));

  const layoutConflictModalPopup = usePopupState<{
    userLayout: LayoutRecord;
    sharedLayout: LayoutRecord;
  }>();

  useSyncTimerAndSearchParams(workspaceContext);

  const { error: layoutHydrationError } = useLayoutHydration(workspaceContext);

  React.useEffect(
    function reportHydrationError() {
      if (!layoutHydrationError) {
        return;
      }

      const notification: Notification = {
        id: layoutHydrationError.id,
        message: layoutHydrationError.message,
        severity: "error",
      };
      setWorkspaceNotifications((prevNotifications) =>
        withNewNotifications(prevNotifications, [notification]),
      );
    },
    [layoutHydrationError],
  );

  const { sessions, sessionLoadFailures } = workspaceContext;
  React.useEffect(
    function reportSessionOutcomes() {
      const sessionNotifications = sessionNotices(
        sessions,
        sessionLoadFailures,
      ).map(notificationFor);
      setWorkspaceNotifications((prevNotifications) =>
        withNewNotifications(prevNotifications, sessionNotifications),
      );
    },
    [sessions, sessionLoadFailures],
  );

  const openLayoutConflictModal = layoutConflictModalPopup.open;
  React.useEffect(
    function handleLayoutConflictError() {
      const configSource = visualizerInitData.configSource;
      if (
        configSource?.type === "recoverable-error" &&
        configSource.value.name === LAYOUT_CONFLICT_ERROR_NAME
      ) {
        try {
          const { userLayout, sharedLayout } =
            LayoutService.parseRobotoLayoutConflictException(
              configSource.value.metadata?.conflictingLayouts,
            );

          openLayoutConflictModal({
            metadata: { userLayout, sharedLayout },
          });
        } catch (e) {
          // Swallow and report the programming error
          ErrorMonitoringService.captureError(e);
        }
      }
    },
    [openLayoutConflictModal, visualizerInitData],
  );

  const { showChromiumSuggestion, hideChromiumSuggestion } =
    useSuggestChromium();
  const browser = getBrowser();

  return (
    <EphemeralWorkspaceStateContext.Provider value={workspaceContext}>
      <PageLayout
        title={"Roboto - Visualization"}
        headerContentLeft={<DatasourceInfo />}
        headerContentRight={<Header />}
      >
        <div className={styles.workspaceContainer}>
          {showChromiumSuggestion && (
            <Alert
              severity="warning"
              onClose={hideChromiumSuggestion}
              className={styles.chromiumSuggestionAlert}
            >
              <AlertTitle>
                Roboto recommends Chrome for data visualization.
              </AlertTitle>
              <p>
                We&apos;ve measured best performance on Chrome and other
                Chromium-based browsers for loading large amounts of time series
                data in Roboto.{" "}
                {browser === "Mozilla Firefox" && (
                  <>
                    The Mozilla team is working to improve Firefox performance (
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      href="https://bugzilla.mozilla.org/show_bug.cgi?id=1800713"
                    >
                      link
                    </a>
                    ).{" "}
                  </>
                )}
              </p>
              <p>
                Consider switching to a Chromium-based browser if the data load
                is interfering with your workflow.
              </p>
            </Alert>
          )}
          <Notifications
            onDismiss={(notification) => {
              setWorkspaceNotifications((prevNotifications) =>
                prevNotifications.filter((n) => n.id !== notification.id),
              );
            }}
            notifications={workspaceNotifications}
          />
          <DndContainer>
            <div className={styles.primaryContent}>
              <Sidebar />
              <PanelBoard
                className={styles.panelBoard}
                errorMsg={workspaceContext.error?.message}
              />
            </div>
          </DndContainer>
          <Timeline />
        </div>

        <LayoutConflictModal
          open={layoutConflictModalPopup.isOpen}
          conflictingLayouts={layoutConflictModalPopup.metadata}
          onClose={layoutConflictModalPopup.close}
          onLayoutRenamed={workspaceContext.setActiveLayout}
        />
      </PageLayout>
    </EphemeralWorkspaceStateContext.Provider>
  );
}
